import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, TimeSeries } from '../ui';
import { closeTimeSeries } from '../../state/uiSlice';
import { useMetricSeries } from '../../api/hooks';
import { inr, num, pct } from '../../lib/format';

const PERIODS = [
  { n: 12, label: '1Y' },
  { n: 36, label: '3Y' },
  { n: 60, label: '5Y' },
  { n: 240, label: 'Max' },
];

/**
 * `type` says what kind of quantity the series carries, not how big it is.
 * Ratios — Sharpe, Sortino, beta, a factor loading — are plain numbers; without
 * their own kind they fell to the currency default and a Sharpe of 0.8 was
 * quoted as ₹0.80.
 */
function fmt(value, type) {
  /* Two places, not one. These series are mostly CONTRIBUTIONS — a holding's
     slice of a fund's risk — which live below 1%: a VaR contribution of -0.21%
     and one of -0.24% both read as "-0.2%" at one place, so the axis labelled
     two visibly different points with the same number and the high/low readout
     quoted a range whose ends looked equal. */
  if (type === 'pct') return pct(value, 2);
  if (type === 'num') return num(value, 2);
  if (type === 'count') return num(value);
  /* A share price keeps its paise and is never abbreviated to lakhs. */
  if (type === 'price') return inr(value, { decimals: 2 });
  return inr(value);
}

/**
 * A metric key as a reader would say it: the key names the series to fetch, not
 * the thing being charted. "fundFactor.weight" is a weight; "risk.trackingError"
 * is a tracking error. The scope — which fund, which holding — is already said
 * beside it, so only the measure's own name is left.
 */
const readableMetric = (key) => {
  const last = String(key || '').split('.').pop() || '';
  const words = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words || 'this metric';
};

/**
 * Universal time-series modal — opened by any <Num metricKey=… />.
 * The series is real: it comes from the backend history endpoint that owns the
 * metric. Metrics with no history endpoint say so instead of drawing a line.
 */
export default function TimeSeriesModal() {
  const dispatch = useDispatch();
  const ctx = useSelector((s) => s.ui.timeSeries);
  const [months, setMonths] = useState(36);

  const series = useMetricSeries({
    metricKey: ctx?.metricKey,
    entity: ctx?.entity,
    holding: ctx?.holding,
    scenario: ctx?.scenario,
    fund: ctx?.fund,
    months,
  });
  const { supported, loading, error, points } = series;

  if (!ctx) return null;

  /* The caller's own events, or the ones the series came with — a stock's
     price arrives with the fund's trades in it already dated to the window. */
  const markers = ctx.markers ?? series.markers ?? [];
  /* Drawn only when it has a line's worth of readings, so a stock the pass
     never weighted does not earn an empty right-hand axis. */
  const overlay = (series.overlay?.points?.length ?? 0) >= 2 ? series.overlay : null;

  const values = points.map((p) => p.value);
  /* Kept for the High / Low readout — the chart works out its own scale. */
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const firstV = values[0];
  const lastV = values[values.length - 1];
  const changePct = firstV ? ((lastV - firstV) / Math.abs(firstV)) * 100 : null;
  /* A caller that opened the chart from a figure passes that figure; one that
     opened it from a row has nothing to pass, and the series' own last reading
     is the current value. */
  const current = ctx.raw ?? (lastV == null ? '—' : fmt(lastV, ctx.type));

  return (
    <Modal
      open
      width={720}
      title={ctx.label || 'History'}
      onClose={() => dispatch(closeTimeSeries())}
      footer={
        <>
          <button className="btn" onClick={() => dispatch(closeTimeSeries())}>Close</button>
          <button className="btn pri" onClick={() => dispatch(closeTimeSeries())}>Done</button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div className="tabs">
          {PERIODS.map((p) => (
            <button key={p.n} className={months === p.n ? 'on' : ''} onClick={() => setMonths(p.n)}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="ts-meta">
          <div>Current<b>{current}</b></div>
          <div>Period change<b className={changePct > 0 ? 'up' : changePct < 0 ? 'down' : ''}>
            {changePct == null ? '—' : pct(changePct)}
          </b></div>
          <div>High / Low<b>
            {values.length ? `${fmt(max, ctx.type)} / ${fmt(min, ctx.type)}` : '—'}
          </b></div>
        </div>
      </div>

      <div className="ts-chart-wrap">
        {!supported ? (
          <p style={{ fontSize: 12.5, color: 'var(--ink2)', padding: '28px 4px', textAlign: 'center', lineHeight: 1.6 }}>
            {String(ctx.metricKey).startsWith('count.') ? (
              <>
                <b>{ctx.label}</b> is a point-in-time count, recomputed from today's book. Nothing
                stores how many portfolios tripped a guard on a past date, so there is no series to
                draw. The figures it is derived from — portfolio value, risk metrics, holdings — each
                have their own history.
              </>
            ) : (
              <>
                No history is published for <b>{readableMetric(ctx.metricKey)}</b>. Portfolio value and return,
                realised risk metrics, fund NAV and AUM, per-holding weight and value, factor
                exposures and book roll-ups all have one — this metric would need its own endpoint.
              </>
            )}
          </p>
        ) : loading ? (
          <p style={{ fontSize: 12.5, color: 'var(--ink3)', padding: '28px 4px', textAlign: 'center' }}>
            Loading history…
          </p>
        ) : error ? (
          <p style={{ fontSize: 12.5, color: 'var(--red)', padding: '28px 4px', textAlign: 'center' }}>
            {error.message}
          </p>
        ) : !points.length ? (
          <p style={{ fontSize: 12.5, color: 'var(--ink3)', padding: '28px 4px', textAlign: 'center', lineHeight: 1.6 }}>
            {String(ctx.metricKey).startsWith('stock.') ? (
              /* A fund's book has lines that are not listed stocks — debt,
                 liquid-fund units, cash — and the price master has nothing
                 for them. That is the line's nature, not a gap in the data. */
              <>
                No listed price for <b>{ctx.entity}</b>. Only an exchange-traded stock has a price
                series; debt, cash and fund units in the book do not.
              </>
            ) : (
              <>No history recorded for this entity in the selected window.</>
            )}
          </p>
        ) : points.length < 2 ? (
          /* A line needs two points; TimeSeries draws nothing with one and says
             so to nobody, which read as a broken chart. State the single
             reading instead — it is data, just not yet a series. */
          <p style={{ fontSize: 12.5, color: 'var(--ink2)', padding: '28px 4px', textAlign: 'center', lineHeight: 1.6 }}>
            One reading published so far — <b>{fmt(points[0].value, ctx.type)}</b> on{' '}
            <b>{points[0].date}</b>. A line needs at least two; this series gains a point
            each time the source publishes.
          </p>
        ) : (
          <>
            <TimeSeries
              points={points}
              height={240}
              format={(v) => fmt(v, ctx.type)}
              label={ctx.label || 'Value'}
              markers={markers}
              /* A second line on its own right-hand axis, when the series came
                 with one — a fund's weight in the stock over the stock's price. */
              overlay={overlay ? { ...overlay, format: (v) => fmt(v, overlay.type) } : null}
            />
            {/* A dot with no key is a mystery; say which way each one went, and
                which line each axis belongs to. */}
            {(markers.length > 0 || overlay) && (
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 11.5, color: 'var(--ink2)', marginTop: 8 }}>
                {overlay && (
                  <>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 14, height: 2, background: 'var(--brand)', display: 'inline-block' }} />
                      {readableMetric(ctx.metricKey)} · left axis
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 14, height: 2, background: 'var(--amber)', display: 'inline-block' }} />
                      {overlay.label.toLowerCase()} · right axis
                    </span>
                  </>
                )}
                {markers.length > 0 && (
                  <>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
                      bought — added or increased
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--red)', display: 'inline-block' }} />
                      sold — trimmed or exited
                    </span>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* The scenario is named because it is what separates two otherwise
          identical charts: the same metric under a stress replay and under the
          base run are different series off the same subject. */}
      <div style={{ fontSize: 12, color: 'var(--ink2)', fontFamily: "'Source Serif 4',Georgia,serif", lineHeight: 1.55 }}>
        {ctx.entity ? <>Series for <b>{ctx.entity}</b></> : <>Metric</>}
        {ctx.holding ? <> · holding <b>{ctx.holding}</b></> : null}
        {ctx.fund ? <> · trades of <b>{ctx.fund}</b></> : null}
        {ctx.scenario ? <> · scenario <b>{ctx.scenario}</b></> : null}
        {' · '}{readableMetric(ctx.metricKey)}.
      </div>
    </Modal>
  );
}
